import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { portalAPI } from '../services/api';

const PortalAuthContext = createContext(null);

export const usePortalAuth = () => {
  const context = useContext(PortalAuthContext);
  if (!context) throw new Error('usePortalAuth must be used within PortalAuthProvider');
  return context;
};

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'portalAccessToken',
  REFRESH_TOKEN: 'portalRefreshToken',
  USER: 'portalUser',
};

export const PortalAuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.USER);
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      if (!token) { setLoading(false); return; }
      const { data } = await portalAPI.auth.me();
      setUser(data);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data));
    } catch (error) {
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const login = async (email, password) => {
    const { data } = await portalAPI.auth.login({ email, password });
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
    // Also set generic keys so the api interceptor picks them up
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try { await portalAPI.auth.logout(); } catch (_) {}
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
  };

  const value = { user, loading, login, logout, loadProfile };

  return <PortalAuthContext.Provider value={value}>{children}</PortalAuthContext.Provider>;
};
